import { LessonContentSchema } from "../../components/education/LessonTemplateRenderer";

// Sample lesson content schemas demonstrating the structured content format
export const lessonContentSchemas: Record<string, LessonContentSchema> = {
  "color-basics": {
    version: "1.0.0",
    metadata: {
      title: "Color Basics Introduction",
      description:
        "Learn the fundamentals of color theory, including the color wheel, primary colors, and basic color relationships.",
      difficulty: "beginner",
      estimatedDuration: 45,
      category: "Foundations",
      tags: ["color-theory", "basics", "color-wheel"],
    },
    objectives: [
      "Understand the color wheel structure",
      "Identify primary, secondary, and tertiary colors",
      "Learn basic color terminology",
      "Explore color relationships",
    ],
    outcomes: [
      "Ability to identify and name colors",
      "Understanding of basic color mixing",
      "Foundation for advanced color theory",
    ],
    prerequisites: [],
    pages: [
      {
        id: "color-basics-page-1",
        title: "Introduction to Color Theory",
        description: "Welcome to your journey into understanding color!",
        content: [
          {
            type: "heading",
            content: "What is Color Theory?",
            metadata: { level: 1 },
          },
          {
            type: "text",
            content:
              "Color theory is both the science and art of using color. It explains how humans perceive color, how colors mix, match or clash, the subliminal messages color communicates, and the methods used to replicate color.",
          },
          {
            type: "callout",
            content:
              "Color is not just about aesthetics - it's a powerful tool that influences emotions, behavior, and decision-making in design.",
            metadata: { variant: "info" },
          },
          {
            type: "heading",
            content: "Why Study Color Theory?",
            metadata: { level: 2 },
          },
          {
            type: "list",
            content: [
              "Create visually appealing designs",
              "Communicate messages effectively",
              "Ensure accessibility and readability",
              "Build brand recognition",
              "Understand cultural color significance",
            ],
            metadata: { ordered: false },
          },
        ],
        requiredForCompletion: true,
        objectives: ["Understand what color theory is and why it matters"],
      },
      {
        id: "color-basics-page-2",
        title: "The Color Wheel",
        description:
          "Explore the fundamental tool for understanding color relationships.",
        content: [
          {
            type: "heading",
            content: "Understanding the Color Wheel",
            metadata: { level: 1 },
          },
          {
            type: "text",
            content:
              "The color wheel is a circular diagram that shows the relationships between primary colors, secondary colors, and tertiary colors. It's the foundation of color theory and an essential tool for designers.",
          },
          {
            type: "heading",
            content: "Primary Colors",
            metadata: { level: 2 },
          },
          {
            type: "text",
            content:
              "Primary colors are the three pigment colors that cannot be mixed or formed by any combination of other colors. All other colors are derived from these three hues.",
          },
          {
            type: "callout",
            content:
              "In traditional color theory (RYB model), the primary colors are Red, Yellow, and Blue. In digital design (RGB model), they are Red, Green, and Blue.",
            metadata: { variant: "tip" },
          },
        ],
        interactiveElements: [
          {
            id: "color-wheel-demo",
            type: "color-wheel",
            title: "Interactive Color Wheel",
            description:
              "Explore the color wheel and see how colors relate to each other.",
            required: true,
            completionCriteria: {
              type: "interaction",
              threshold: 1,
            },
          },
        ],
        requiredForCompletion: true,
        objectives: [
          "Identify primary colors and understand the color wheel structure",
        ],
      },
      {
        id: "color-basics-page-3",
        title: "Color Relationships",
        description: "Learn how colors interact and relate to each other.",
        content: [
          {
            type: "heading",
            content: "Understanding Color Relationships",
            metadata: { level: 1 },
          },
          {
            type: "text",
            content:
              "Colors have specific relationships with each other that create harmony or contrast. Understanding these relationships helps you create effective color schemes.",
          },
          {
            type: "heading",
            content: "Color Harmonies",
            metadata: { level: 2 },
          },
          {
            type: "list",
            content: [
              "Complementary: Colors opposite each other on the color wheel",
              "Analogous: Colors next to each other on the color wheel",
              "Triadic: Three colors evenly spaced on the color wheel",
              "Split-Complementary: A base color and two adjacent to its complement",
            ],
            metadata: { ordered: false },
          },
        ],
        interactiveElements: [
          {
            id: "harmony-explorer",
            type: "harmony-explorer",
            title: "Color Harmony Explorer",
            description:
              "Experiment with different color harmony combinations.",
            required: true,
            completionCriteria: {
              type: "interaction",
              threshold: 1,
            },
          },
        ],
        requiredForCompletion: true,
        objectives: ["Understand basic color harmonies"],
      },
      {
        id: "color-basics-page-4",
        title: "Practice Quiz",
        description: "Test your knowledge of color basics.",
        content: [
          {
            type: "heading",
            content: "Knowledge Check",
            metadata: { level: 1 },
          },
          {
            type: "text",
            content:
              "Let's review what you've learned about color theory basics with a quick quiz.",
          },
        ],
        interactiveElements: [
          {
            id: "color-basics-quiz",
            type: "quiz",
            title: "Color Basics Quiz",
            description:
              "Test your knowledge of color fundamentals with 5 questions covering primary colors, secondary colors, and basic color relationships.",
            required: true,
            props: {
              quizId: "color-basics-quiz",
              timeLimit: 15,
              passingScore: 80,
            },
            completionCriteria: {
              type: "score",
              threshold: 80,
            },
          },
        ],
        requiredForCompletion: true,
        objectives: ["Demonstrate understanding of color basics concepts"],
      },
    ],
  },

  "color-wheel": {
    version: "1.0.0",
    metadata: {
      title: "Color Wheel Fundamentals",
      description:
        "Deep dive into the color wheel, its history, and how to use it as a tool for creating harmonious color schemes.",
      difficulty: "beginner",
      estimatedDuration: 60,
      category: "Foundations",
      tags: ["color-wheel", "harmony", "design-tools"],
    },
    objectives: [
      "Master color wheel navigation",
      "Understand color temperature",
      "Learn about color value and saturation",
      "Practice color identification",
    ],
    outcomes: [
      "Proficient use of color wheel",
      "Understanding of warm vs cool colors",
      "Ability to create basic color schemes",
    ],
    prerequisites: ["color-basics"],
    pages: [
      {
        id: "color-wheel-page-1",
        title: "Advanced Color Wheel Concepts",
        description: "Go deeper into color wheel theory and applications.",
        content: [
          {
            type: "heading",
            content: "Beyond the Basics",
            metadata: { level: 1 },
          },
          {
            type: "text",
            content:
              "Now that you understand the color wheel basics, let's explore more advanced concepts that will help you create sophisticated color schemes.",
          },
          {
            type: "heading",
            content: "Color Temperature",
            metadata: { level: 2 },
          },
          {
            type: "text",
            content:
              "Color temperature refers to the warmth or coolness of a color. Warm colors (reds, oranges, yellows) tend to advance and create energy, while cool colors (blues, greens, purples) tend to recede and create calm.",
          },
        ],
        requiredForCompletion: true,
        objectives: ["Understand color temperature and its effects"],
      },
      {
        id: "color-wheel-page-2",
        title: "Practical Applications",
        description: "Apply color wheel knowledge to real design scenarios.",
        content: [
          {
            type: "heading",
            content: "Using the Color Wheel in Design",
            metadata: { level: 1 },
          },
          {
            type: "callout",
            content:
              "Practice creating color schemes for different design contexts: a corporate website, a children's app, and a luxury brand.",
            metadata: { variant: "exercise" },
          },
        ],
        interactiveElements: [
          {
            id: "color-wheel-advanced-quiz",
            type: "quiz",
            title: "Color Wheel Advanced Quiz",
            description:
              "Challenge yourself with questions about color temperature, tertiary colors, and practical applications.",
            required: true,
            props: {
              quizId: "color-wheel-quiz",
              timeLimit: 20,
              passingScore: 80,
            },
            completionCriteria: {
              type: "score",
              threshold: 80,
            },
          },
          {
            id: "color-scheme-builder",
            type: "color-mixer",
            title: "Color Scheme Builder",
            description:
              "Build and test different color schemes for various design contexts.",
            required: true,
            completionCriteria: {
              type: "interaction",
              threshold: 1,
            },
          },
        ],
        requiredForCompletion: true,
        objectives: ["Apply color wheel theory to practical design scenarios"],
      },
    ],
  },
};

// Helper function to get lesson content schema
export const getLessonContentSchema = (
  lessonId: string,
): LessonContentSchema | null => {
  return lessonContentSchemas[lessonId] || null;
};
